import React, { useState, useEffect } from 'react';
import { 
    Dumbbell, Plus, Trash2, Edit, QrCode, X, BadgeCheck, 
    Calendar, Clock, Users, CheckCircle, AlertCircle, Sparkles, 
    LayoutGrid, CalendarDays, ChevronRight, UserCheck, RefreshCw 
} from 'lucide-react';
import api from '../services/api';
import ClientBadgeModal from '../components/ClientBadgeModal';
import CoachScheduleModal from '../components/CoachScheduleModal';

const DAYS_OF_WEEK = [
    { id: 1, name: 'Lundi', short: 'Lun' },
    { id: 2, name: 'Mardi', short: 'Mar' },
    { id: 3, name: 'Mercredi', short: 'Mer' },
    { id: 4, name: 'Jeudi', short: 'Jeu' },
    { id: 5, name: 'Vendredi', short: 'Ven' },
    { id: 6, name: 'Samedi', short: 'Sam' },
    { id: 7, name: 'Dimanche', short: 'Dim' },
];

const Coaches = () => {
    const [coaches, setCoaches] = useState<any[]>([]);
    const [presenceData, setPresenceData] = useState<any>(null);
    const [allSchedules, setAllSchedules] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'coaches' | 'planning'>('coaches');

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        description: '',
        specialties: ''
    });
    const [badgeCoach, setBadgeCoach] = useState<any>(null);
    const [scheduleCoach, setScheduleCoach] = useState<any>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [coachesRes, presenceRes, schedulesRes] = await Promise.all([
                api.get('/coaches'),
                api.get('/coaches/presence/today').catch(() => ({ data: null })),
                api.get('/coaches/schedules/all').catch(() => ({ data: [] }))
            ]);
            setCoaches(coachesRes.data || []);
            setPresenceData(presenceRes.data || null);
            setAllSchedules(schedulesRes.data || []);
        } catch (error) {
            console.error("Erreur de récupération des coachs et planning", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // Rafraîchir toutes les 60 secondes pour mettre à jour la présence en direct
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleCreateCoach = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/coaches', formData);
            setFormData({ first_name: '', last_name: '', description: '', specialties: '' });
            setIsFormOpen(false);
            fetchData();
        } catch (error) {
            console.error("Erreur lors de la création", error);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Voulez-vous vraiment supprimer ce coach ?")) {
            try {
                await api.delete(`/coaches/${id}`);
                fetchData();
            } catch (error) {
                console.error("Erreur lors de la suppression", error);
            }
        }
    };

    // Statistiques de présence aujourd'hui
    const currentDayName = presenceData?.currentDayName || 'Aujourd\'hui';
    const currentDayOfWeek = presenceData?.currentDayOfWeek || ((new Date().getDay() === 0) ? 7 : new Date().getDay());
    const enrichedCoaches = presenceData?.coaches || coaches;

    const presentNowCoaches = enrichedCoaches.filter((c: any) => c.presenceStatus === 'present');
    const scheduledLaterCoaches = enrichedCoaches.filter((c: any) => c.presenceStatus === 'scheduled_later');

    return (
        <div className="space-y-6">
            {/* Header principal */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-brand-dark flex items-center gap-3">
                        <Dumbbell className="text-brand-primary" size={32} />
                        Gestion & Planning des Coachs
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Programmation hebdomadaire et suivi en temps réel de la présence des coachs en salle.
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={fetchData}
                        className="p-2.5 bg-white border border-gray-200 text-gray-700 hover:text-brand-dark hover:bg-gray-50 rounded-lg transition-colors shadow-2xs"
                        title="Actualiser les présences"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={() => setIsFormOpen(!isFormOpen)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-brand-dark font-black rounded-lg hover:bg-yellow-400 transition-colors shadow-sm"
                    >
                        <Plus size={20} />
                        {isFormOpen ? 'Fermer formulaire' : 'Ajouter un coach'}
                    </button>
                </div>
            </div>

            {/* BANDEAU EN TEMPS RÉEL : COACHS PRÉSENTS EN SALLE AUJOURD'HUI */}
            <div className="bg-gradient-to-r from-gray-900 via-brand-dark to-gray-900 rounded-2xl p-5 md:p-6 text-white shadow-md border border-gray-800">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-gray-800/80 pb-4 mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shadow-inner">
                            <UserCheck size={26} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                <h2 className="text-lg font-black tracking-wide">
                                    Coachs actuellement en salle de sport
                                </h2>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                                Jour : <strong className="text-brand-primary">{currentDayName}</strong> | Heure : <strong className="text-white font-mono">{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</strong>
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs">
                        <div className="px-3.5 py-1.5 rounded-lg bg-emerald-950/70 border border-emerald-800 text-emerald-300 font-bold flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                            <span>{presentNowCoaches.length} en service maintenant</span>
                        </div>
                        <div className="px-3.5 py-1.5 rounded-lg bg-amber-950/70 border border-amber-800 text-amber-300 font-bold flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                            <span>{scheduledLaterCoaches.length} prévu(s) plus tard</span>
                        </div>
                    </div>
                </div>

                {/* Liste des coachs actuellement en salle */}
                {presentNowCoaches.length === 0 ? (
                    <div className="py-4 px-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-3 text-sm text-gray-300">
                            <AlertCircle size={20} className="text-amber-400 shrink-0" />
                            <span>
                                Aucun coach n'a de créneau actif en ce moment pour ce <strong>{currentDayName}</strong>.
                            </span>
                        </div>
                        {scheduledLaterCoaches.length > 0 && (
                            <span className="text-xs text-amber-300 font-bold">
                                Prochain coach à : {scheduledLaterCoaches[0].todaySchedules?.[0]?.start_time?.substring(0, 5)} ({scheduledLaterCoaches[0].first_name})
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {presentNowCoaches.map((c: any) => (
                            <div 
                                key={c.id} 
                                className="bg-white/10 hover:bg-white/15 border border-emerald-500/40 rounded-xl p-3.5 flex items-center justify-between transition-all"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        {c.photo_url ? (
                                            <img src={c.photo_url} alt="" className="w-11 h-11 rounded-lg object-cover border border-emerald-400/50" />
                                        ) : (
                                            <div className="w-11 h-11 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-black text-sm border border-emerald-500/40">
                                                {c.first_name[0]}{c.last_name[0]}
                                            </div>
                                        )}
                                        <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-gray-900" title="En salle"></span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm text-white">
                                            {c.first_name} {c.last_name}
                                        </h3>
                                        <p className="text-[11px] text-emerald-300 font-medium">
                                            {c.activeSlot 
                                                ? `${c.activeSlot.start_time?.substring(0, 5)} - ${c.activeSlot.end_time?.substring(0, 5)}`
                                                : c.todaySchedules?.[0]
                                                    ? `${c.todaySchedules[0].start_time?.substring(0, 5)} - ${c.todaySchedules[0].end_time?.substring(0, 5)}`
                                                    : 'En service'}
                                            {c.activeSlot?.role_note ? ` • ${c.activeSlot.role_note}` : ''}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            {c.hasCheckedInToday ? (
                                                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-700">
                                                    ✓ Badge pointé ({new Date(c.lastCheckIn).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})
                                                </span>
                                            ) : (
                                                <span className="text-[10px] font-medium text-gray-300 bg-white/5 px-2 py-0.5 rounded">
                                                    Programmé au planning
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setScheduleCoach(c)}
                                    className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                                    title="Voir ou modifier le planning"
                                >
                                    <Calendar size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* FORMULAIRE NOUVEAU COACH */}
            {isFormOpen && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
                        <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                            <Plus size={20} className="text-brand-primary" />
                            Nouveau Coach
                        </h2>
                        <button onClick={() => setIsFormOpen(false)} className="text-gray-400 hover:text-gray-600">
                            <X size={20} />
                        </button>
                    </div>

                    <form onSubmit={handleCreateCoach} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Prénom *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-sm font-medium"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nom *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-sm font-medium"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Spécialités</label>
                                <input
                                    type="text"
                                    placeholder="Ex: Fitness, Musculation, Boxing, Cardio, Crossfit"
                                    value={formData.specialties}
                                    onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-sm"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Description / Notes</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-sm"
                                    rows={2}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button 
                                type="button" 
                                onClick={() => setIsFormOpen(false)}
                                className="px-5 py-2 border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 text-xs transition-colors"
                            >
                                Annuler
                            </button>
                            <button 
                                type="submit" 
                                className="px-6 py-2 bg-brand-dark text-white font-black rounded-lg hover:bg-gray-800 text-xs transition-colors shadow-sm"
                            >
                                Enregistrer le coach
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ONGLETS DE NAVIGATION */}
            <div className="flex items-center justify-between border-b border-gray-200">
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('coaches')}
                        className={`flex items-center gap-2 px-5 py-3 font-bold text-sm border-b-2 transition-all cursor-pointer ${
                            activeTab === 'coaches'
                                ? 'border-brand-primary text-brand-dark'
                                : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <Users size={18} />
                        Équipe des Coachs ({coaches.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('planning')}
                        className={`flex items-center gap-2 px-5 py-3 font-bold text-sm border-b-2 transition-all cursor-pointer ${
                            activeTab === 'planning'
                                ? 'border-brand-primary text-brand-dark'
                                : 'border-transparent text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <CalendarDays size={18} />
                        Planning Hebdomadaire (Grille des jours)
                    </button>
                </div>
            </div>

            {/* VUE 1 : CARTES DES COACHS AVEC LEUR PLANNING HEBDOMADAIRE */}
            {activeTab === 'coaches' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        <p className="text-gray-500 col-span-3 py-10 text-center">Chargement des coachs...</p>
                    ) : enrichedCoaches.length === 0 ? (
                        <div className="col-span-3 py-12 text-center bg-white rounded-2xl border border-gray-100 p-8">
                            <Dumbbell size={48} className="mx-auto text-gray-300 mb-3" />
                            <h3 className="text-base font-bold text-gray-700">Aucun coach enregistré</h3>
                            <p className="text-xs text-gray-500 mt-1 mb-4">Ajoutez un premier entraîneur pour configurer son planning.</p>
                            <button
                                onClick={() => setIsFormOpen(true)}
                                className="px-4 py-2 bg-brand-primary text-brand-dark font-black rounded-lg text-xs"
                            >
                                + Ajouter un coach
                            </button>
                        </div>
                    ) : (
                        enrichedCoaches.map((coach: any) => {
                            // Jours programmés pour ce coach
                            const weeklySchedules: any[] = coach.allWeeklySchedules || [];
                            const activeDaysSet = new Set(weeklySchedules.map((s: any) => s.day_of_week));

                            return (
                                <div key={coach.id} className="bg-white rounded-2xl shadow-xs border border-gray-200/90 overflow-hidden flex flex-col hover:shadow-md transition-all">
                                    {/* Header de la carte */}
                                    <div className="h-28 bg-gradient-to-r from-gray-100 via-yellow-50/50 to-gray-100 w-full relative flex items-center justify-center p-4">
                                        {coach.photo_url ? (
                                            <img src={coach.photo_url} alt="" className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-sm" />
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-brand-primary/20 text-brand-dark flex items-center justify-center font-black text-2xl border-4 border-white shadow-sm">
                                                {coach.first_name[0]}{coach.last_name[0]}
                                            </div>
                                        )}

                                        {/* Pastille de statut en direct */}
                                        <div className="absolute top-3 right-3">
                                            {coach.presenceStatus === 'present' ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500 text-white font-extrabold text-[11px] rounded-full shadow-xs">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                                    En salle
                                                </span>
                                            ) : coach.presenceStatus === 'scheduled_later' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 font-bold text-[11px] rounded-full">
                                                    Prévu aujourd'hui
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-500 font-medium text-[11px] rounded-full">
                                                    Repos aujourd'hui
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Contenu */}
                                    <div className="p-5 flex-1 flex flex-col justify-between">
                                        <div>
                                            <div className="text-center mb-3">
                                                <h3 className="text-xl font-black text-gray-900">
                                                    {coach.first_name} {coach.last_name}
                                                </h3>
                                                <p className="text-xs font-bold text-brand-primary uppercase tracking-wider mt-0.5">
                                                    {coach.specialties || 'Coach Fitness'}
                                                </p>
                                                {coach.description && (
                                                    <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                                                        {coach.description}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Grille des 7 jours de présence */}
                                            <div className="mt-4 pt-3 border-t border-gray-100">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                                                        <Calendar size={13} />
                                                        Jours de présence :
                                                    </span>
                                                    <span className="text-[11px] font-bold text-gray-700">
                                                        {activeDaysSet.size} / 7 jours
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-7 gap-1">
                                                    {DAYS_OF_WEEK.map(d => {
                                                        const isWorkDay = activeDaysSet.has(d.id);
                                                        const isToday = d.id === currentDayOfWeek;
                                                        return (
                                                            <div
                                                                key={d.id}
                                                                className={`py-1.5 text-center rounded-lg text-[11px] font-black transition-all ${
                                                                    isWorkDay
                                                                        ? isToday 
                                                                            ? 'bg-brand-primary text-brand-dark ring-2 ring-yellow-400' 
                                                                            : 'bg-emerald-100 text-emerald-800'
                                                                        : 'bg-gray-100 text-gray-400'
                                                                }`}
                                                                title={`${d.name} : ${isWorkDay ? 'Programmé' : 'Repos'}`}
                                                            >
                                                                {d.short}
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Détail des créneaux d'aujourd'hui si programmé */}
                                                {coach.todaySchedules && coach.todaySchedules.length > 0 && (
                                                    <div className="mt-3 p-2 bg-yellow-50/70 border border-yellow-200/80 rounded-lg text-xs">
                                                        <span className="font-bold text-yellow-900 block">
                                                            Aujourd'hui ({currentDayName}) :
                                                        </span>
                                                        <div className="space-y-0.5 mt-1 text-gray-700">
                                                            {coach.todaySchedules.map((s: any, idx: number) => (
                                                                <div key={idx} className="flex items-center justify-between text-[11px]">
                                                                    <span className="font-mono font-bold">
                                                                        {s.start_time?.substring(0, 5)} - {s.end_time?.substring(0, 5)}
                                                                    </span>
                                                                    <span className="text-gray-500 truncate max-w-[140px]">
                                                                        {s.role_note || 'Présence en salle'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="mt-5 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                                            <button 
                                                type="button"
                                                onClick={() => setScheduleCoach(coach)} 
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-brand-primary/20 hover:bg-brand-primary text-brand-dark font-black rounded-lg text-xs transition-colors cursor-pointer"
                                                title="Programmer les jours et horaires de la semaine"
                                            >
                                                <Calendar size={15} />
                                                <span>Programmer</span>
                                            </button>

                                            <button 
                                                type="button"
                                                onClick={() => setBadgeCoach(coach)} 
                                                className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg text-xs transition-colors cursor-pointer"
                                                title="Voir et imprimer le badge officiel"
                                            >
                                                <BadgeCheck size={15} className="text-amber-600" />
                                                <span>Badge</span>
                                            </button>

                                            <button 
                                                type="button"
                                                onClick={() => handleDelete(coach.id)} 
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer" 
                                                title="Supprimer ce coach"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* VUE 2 : GRILLE COMPLÈTE DU PLANNING HEBDOMADAIRE (LUNDI -> DIMANCHE) */}
            {activeTab === 'planning' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200/90 overflow-hidden">
                    <div className="p-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50/70">
                        <div>
                            <h2 className="text-base font-black text-gray-800 flex items-center gap-2">
                                <CalendarDays className="text-brand-primary" size={20} />
                                Tableau de bord hebdomadaire des présences
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Vue d'ensemble des coachs programmés pour chaque jour de la semaine.
                            </p>
                        </div>
                        <span className="text-xs font-bold text-gray-600 bg-white px-3 py-1.5 rounded-lg border">
                            Jour actuel : <strong className="text-brand-primary uppercase">{currentDayName}</strong>
                        </span>
                    </div>

                    {/* Grille 7 colonnes pour les 7 jours de la semaine */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 min-h-[420px]">
                        {DAYS_OF_WEEK.map(day => {
                            const isToday = day.id === currentDayOfWeek;
                            const daySchedules = allSchedules.filter(s => s.day_of_week === day.id);

                            return (
                                <div 
                                    key={day.id} 
                                    className={`flex flex-col ${
                                        isToday 
                                            ? 'bg-amber-50/40 ring-2 ring-inset ring-brand-primary/40' 
                                            : 'bg-white'
                                    }`}
                                >
                                    {/* En-tête du jour */}
                                    <div className={`p-3 text-center border-b border-gray-200 ${
                                        isToday ? 'bg-brand-primary text-brand-dark' : 'bg-gray-100 text-gray-700'
                                    }`}>
                                        <div className="font-black text-xs uppercase tracking-wide flex items-center justify-center gap-1.5">
                                            {isToday && <span className="w-2 h-2 rounded-full bg-brand-dark animate-ping"></span>}
                                            <span>{day.name}</span>
                                        </div>
                                        <span className={`text-[10px] font-bold block mt-0.5 ${isToday ? 'text-brand-dark/80' : 'text-gray-500'}`}>
                                            {daySchedules.length} coach(s)
                                        </span>
                                    </div>

                                    {/* Liste des coachs programmés pour ce jour */}
                                    <div className="p-2.5 flex-1 space-y-2 overflow-y-auto max-h-[500px]">
                                        {daySchedules.length === 0 ? (
                                            <div className="py-8 text-center text-gray-400 text-xs italic">
                                                Aucun coach programmé
                                            </div>
                                        ) : (
                                            daySchedules.map(slot => {
                                                const coach = coaches.find(c => c.id === slot.coach_id);
                                                return (
                                                    <div 
                                                        key={slot.id} 
                                                        onClick={() => coach && setScheduleCoach(coach)}
                                                        className="p-2.5 bg-white rounded-xl border border-gray-200/90 shadow-2xs hover:border-brand-primary hover:shadow-xs transition-all cursor-pointer group"
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="font-black text-xs text-gray-900 group-hover:text-brand-dark">
                                                                {slot.first_name} {slot.last_name}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1 text-[11px] font-mono font-bold text-brand-dark bg-yellow-100/70 px-1.5 py-0.5 rounded border border-yellow-200 w-fit">
                                                            <Clock size={11} className="text-brand-primary" />
                                                            <span>{slot.start_time?.substring(0, 5)} - {slot.end_time?.substring(0, 5)}</span>
                                                        </div>
                                                        {slot.role_note && (
                                                            <p className="text-[10px] text-gray-500 mt-1 truncate" title={slot.role_note}>
                                                                {slot.role_note}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Modale de programmation hebdomadaire du coach */}
            <CoachScheduleModal
                isOpen={!!scheduleCoach}
                coach={scheduleCoach}
                onClose={() => setScheduleCoach(null)}
                onSuccess={() => {
                    fetchData();
                }}
            />

            {/* Modale Badge Coach Officiel Hop Fitness */}
            <ClientBadgeModal
                isOpen={!!badgeCoach}
                onClose={() => setBadgeCoach(null)}
                role="coach"
                client={badgeCoach}
            />
        </div>
    );
};

export default Coaches;
